/**
 * Zod schema for user view-preferences payload.
 *
 * Canonical shared location — imported by both the backend API handler
 * (src/api/v4/view-preferences.ts) and the frontend dialog
 * (web/app-home/src/components/app-home-v2/PreferencesDialog.tsx).
 *
 * The TS type is z.infer<typeof UserPrefsSchema>.
 * Do NOT hand-write a parallel interface — it will drift.
 */

import { z } from 'zod';
import { FlowRoleSchema } from '../flow/role';
import {
  FlowAccountLayoutIdSchema,
  FlowHomeLayoutIdSchema,
  FlowListLayoutChoicesSchema,
} from '../flow/flow-layouts/schema';

export const UserPrefsSchema = z.object({
  weekend: z.boolean().optional(),
  days: z.preprocess((v) => Number(v), z.number().int().min(1).max(90)).optional(),
  showInternalMeetings: z.boolean().optional(),
  showCalendarBlocks: z.boolean().optional(),
  copilotDigest: z.boolean().optional(),
  notesMentions: z.boolean().optional(),
  copilotArtifactTypeSlugs: z.array(z.string()).nullable().optional(),
  layoutMode: z.enum(['cards', 'mail']).optional(),
  pipelineLayoutMode: z.enum(['cards', 'mail']).optional(),
  defaultAccountTab: z.enum(['activity', 'intelligence', 'chat']).optional(),
  showAllAccounts: z.boolean().optional(),
  showTeamAccounts: z.boolean().optional(),
  // Live opt-in pref for the Terra Beta flag (nothing is gated on it today).
  terraDesignV2: z.boolean().optional(),
  // Per-rep opt-in to finding phone numbers for contacts that have none.
  findMissingNumbers: z.boolean().optional(),
  // Role view switcher lens. The client sends the chosen team; `null` clears
  // the lens. The server stores NULL when the chosen team equals the user's
  // real team (savePrefs), so GET reports `null` for "no lens".
  viewAsRole: FlowRoleSchema.nullable().optional(),
  // Flow layout choices per surface; `null` clears the saved choice.
  flowLayoutAccount: FlowAccountLayoutIdSchema.nullable().optional(),
  flowLayoutHome: FlowHomeLayoutIdSchema.nullable().optional(),
  flowLayoutList: FlowListLayoutChoicesSchema.nullable().optional(),
});

export type UserPrefsPayload = z.infer<typeof UserPrefsSchema>;

/**
 * Schema for the GET /api/v4/users/me/view-preferences response.
 *
 * The response is a superset of the save payload — extra fields may be
 * present (e.g. userId, createdAt). We use `.passthrough()` so unknown
 * future fields don't cause spurious parse failures. Known fields are
 * coerced/validated; unknowns pass through as-is.
 *
 * Canonical shape — import this into useLayoutPrefs and PreferencesDialog
 * rather than hand-rolling checks. One schema, zero drift.
 */
export const GetPrefsResponseSchema = UserPrefsSchema.extend({
  days: z.preprocess((v) => (v === undefined || v === null ? undefined : Number(v)), z.number().int().min(1).max(90).optional()),
  // User-selectable notification channel (null = default to copilotChannelId)
  // and the resolved copilot channel, so the Preferences dialog can render
  // both the current override and the effective fallback.
  notificationChannelId: z.string().nullable().optional(),
  copilotChannelId: z.string().nullable().optional(),
}).passthrough();

export type GetPrefsResponse = z.infer<typeof GetPrefsResponseSchema>;

/**
 * What GET/PUT/PATCH /api/v4/users/me/view-preferences send: the prefs plus the
 * V4 layout-context aliases (`categories`, `accounts`, `pipeline`, `calendar`).
 * The layout and tab columns are free-text in the DB (no CHECK constraint), so
 * the wire schema reads them as plain strings. The enum on the request schema
 * would turn one legacy value into a 500 on every prefs read.
 */
export const ViewPreferencesResponseSchema = GetPrefsResponseSchema.extend({
  layoutMode: z.string(),
  pipelineLayoutMode: z.string(),
  defaultAccountTab: z.string(),
  categories: z.string(),
  accounts: z.string(),
  pipeline: z.string(),
  calendar: z.string(),
});

/** GET /api/v4/users/me/view-preferences/artifact-type-slugs */
export const ArtifactTypeSlugsResponseSchema = z.array(z.object({ slug: z.string(), label: z.string() }));

/**
 * Request body for PUT /api/v4/users/me/view-preferences/notification-channel.
 * `channel` accepts a raw channel ID, `#name`/bare name, or a Slack archives
 * URL — see classifyChannelInput (src/services/notification-channel-input.ts).
 * `null` resets the pref to default (fall back to copilotChannelId).
 */
export const NotificationChannelSaveRequestSchema = z.object({
  channel: z.string().nullable(),
});

export type NotificationChannelSaveRequest = z.infer<typeof NotificationChannelSaveRequestSchema>;

/**
 * Response for PUT /api/v4/users/me/view-preferences/notification-channel.
 * `notificationChannelId` is always a resolved Slack channel ID (never the
 * raw name/URL the user typed), or `null` after a reset-to-default.
 */
export const NotificationChannelSaveResponseSchema = z.object({
  notificationChannelId: z.string().nullable(),
  channelName: z.string().nullable(),
});

/**
 * Response for POST /api/v4/users/me/view-preferences/notification-channel/test.
 * The route always answers 200 — a failed Slack send is an expected,
 * renderable outcome (the button IS the "can the bot post here" probe), not
 * a server error — so `ok: false` carries the error message rather than the
 * HTTP status.
 */
export const TestNotificationMessageResponseSchema = z.object({
  ok: z.boolean(),
  channelId: z.string().optional(),
  error: z.string().optional(),
});
